import { useState, useCallback, useEffect, useRef } from "react";
import { getTextareaSelectionAnchorViewport } from "../utils/textareaCaretCoordinates";
import { applyComposerTextFormat } from "../utils/applyComposerTextFormat";
import ComposeSelectionFormatToolbar from "../components/MessageHistory/ComposeSelectionFormatToolbar";

/**
 * Barra de formatação flutuante ao selecionar texto no textarea do compositor.
 */
export function useComposeSelectionFormat({ inputRef, setMessage, channelType, formatOptions }) {
  const [anchor, setAnchor] = useState(null);
  const toolbarRef = useRef(null);
  const channelFormats = formatOptions?.[channelType];

  const updateAnchor = useCallback(() => {
    const el = inputRef?.current;
    if (!el || !channelFormats?.length) {
      setAnchor(null);
      return;
    }
    const s = el.selectionStart;
    const e = el.selectionEnd;
    if (s == null || e == null || s === e) {
      setAnchor(null);
      return;
    }
    const p = getTextareaSelectionAnchorViewport(el, s, e);
    if (!p) {
      setAnchor(null);
      return;
    }
    setAnchor({ x: p.x, y: p.y });
  }, [inputRef, channelFormats]);

  useEffect(() => {
    const onScrollOrResize = () => updateAnchor();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [updateAnchor]);

  useEffect(() => {
    const onDocDown = (ev) => {
      if (!anchor) return;
      const ta = inputRef?.current;
      if (ta && (ev.target === ta || ta.contains?.(ev.target))) return;
      if (toolbarRef.current?.contains(ev.target)) return;
      setAnchor(null);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [anchor, inputRef]);

  const applyAndRestore = useCallback(
    (entry) => {
      const ta = inputRef?.current;
      if (!ta) return;
      const s = ta.selectionStart;
      const e = ta.selectionEnd;
      setAnchor(null);
      setMessage((prev) => {
        const result = applyComposerTextFormat(prev, s, e, entry);
        if (!result) return prev;
        queueMicrotask(() => {
          requestAnimationFrame(() => {
            const el = inputRef?.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(result.selStart, result.selEnd);
          });
        });
        return result.next;
      });
    },
    [setMessage, inputRef]
  );

  const composeInputHandlers = {
    onSelect: updateAnchor,
    onMouseUp: updateAnchor,
    onKeyUp: updateAnchor,
    onScroll: updateAnchor,
  };

  const selectionToolbar =
    anchor && channelFormats?.length ? (
      <ComposeSelectionFormatToolbar
        ref={toolbarRef}
        x={anchor.x}
        y={anchor.y}
        formats={channelFormats}
        onApply={applyAndRestore}
      />
    ) : null;

  return { composeInputHandlers, selectionToolbar };
}
